import { useContext, useEffect, useMemo } from 'react';
import DataContext from "../../../context/DataContext.js";
import getScaledValueFactory from "../../../utils/getScaledValueFactory.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { GLYPHSERIES_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
export function BaseGlyphSeries(_ref) {
  let {
    colorAccessor,
    data,
    dataKey,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true,
    renderGlyphs,
    size = 8,
    xAccessor,
    xScale,
    yAccessor,
    yScale
  } = _ref;
  const {
    colorScale,
    theme,
    horizontal
  } = useContext(DataContext);
  const getScaledX = useMemo(() => getScaledValueFactory(xScale, xAccessor), [xScale, xAccessor]);
  const getScaledY = useMemo(() => getScaledValueFactory(yScale, yAccessor), [yScale, yAccessor]);
  const color = colorScale?.(dataKey) ?? theme?.colors?.[0] ?? '#222';
  const ownEventSourceKey = `${GLYPHSERIES_EVENT_SOURCE}-${dataKey}`;
  const eventEmitters = useSeriesEvents({
    dataKey,
    enableEvents,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });
  const glyphs = useMemo(() => data.map((datum, i) => {
    const x = getScaledX(datum);
    if (!isValidNumber(x)) return null;
    const y = getScaledY(datum);
    if (!isValidNumber(y)) return null;
    return {
      key: `${i}`,
      x,
      y,
      color: colorAccessor?.(datum, i) ?? color,
      size: typeof size === 'function' ? size(datum) : size,
      datum
    };
  }).filter(point => point), [color, colorAccessor, data, getScaledX, getScaledY, size]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: renderGlyphs({
      glyphs,
      xScale,
      yScale,
      horizontal,
      ...eventEmitters
    })
  });
}
export default function BaseGlyphSeriesWithRegisteredData(props) {
  const {
    dataKey,
    data,
    xAccessor,
    yAccessor
  } = props;
  const {
    xScale,
    yScale,
    dataRegistry
  } = useContext(DataContext);
  useEffect(() => {
    if (dataRegistry) dataRegistry.registerData({
      key: dataKey,
      data,
      xAccessor,
      yAccessor
    });
    return () => dataRegistry?.unregisterData(dataKey);
  }, [dataRegistry, dataKey, data, xAccessor, yAccessor]);
  const registryEntry = dataRegistry?.get(dataKey);

  // if scales or data are not available in context, render nothing
  if (!xScale || !yScale || !registryEntry) return null;

  // otherwise pass props + over-write data/accessors
  return /*#__PURE__*/_jsx(BaseGlyphSeries, {
    ...props,
    xScale: xScale,
    yScale: yScale,
    data: registryEntry.data,
    xAccessor: registryEntry.xAccessor,
    yAccessor: registryEntry.yAccessor
  });
}